import { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Loader } from "@common/Loader/Loader";
import { useSimpleEirCode } from "./useSimpleEirCode";
import type { FieldValues, UseFormMethods } from "react-hook-form";
import { Grid, Typography } from "@mui/material";
import { eircodeRegex } from "@utils/regex";
import { EircodeHelperText } from "@features/eircode/common/EircodeHelperText/EircodeHelperText";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";

interface IParams<TFieldValues extends FieldValues> {
    addressForm: UseFormMethods<TFieldValues> & Partial<UseFormMethods>;
    prefix?: string;
    isRequired?: boolean;
}

export const SimpleEirCode = <TFieldValues extends FieldValues>(props: IParams<TFieldValues>) => {
    const { addressForm, prefix, isRequired } = props;
    const { t } = useTranslation(["address", "common", "validation"]);

    const {
        formErrors,
        namePrefix,
        idPrefix,
        eirCodeValue,
        submitEircode,
        getEircodeAddressListState,
        onChangeEircode,
        errorMessage,
    } = useSimpleEirCode({ addressForm, prefix });

    const findAddress = (event: FormEvent) => {
        event.preventDefault();
        submitEircode();
    };

    return (
        <Grid columnSpacing={4} container rowSpacing={2}>
            <Grid item md={6} xs={12}>
                <LabeledTextField
                    InputLabelProps={{ shrink: Boolean(eirCodeValue) }}
                    disabled={getEircodeAddressListState.isLoading}
                    error={Boolean(formErrors?.code)}
                    fullWidth
                    helperText={formErrors?.code?.message ?? <EircodeHelperText />}
                    id={`${idPrefix}eircodeText`}
                    inputRef={addressForm.register({
                        required: isRequired && t("validation:required"),
                        pattern: { value: eircodeRegex, message: t("address:validEircode") },
                    })}
                    isRequired={isRequired}
                    labelText={t("address:eircode")}
                    name={`${namePrefix}code`}
                    placeholder={t("address:eircodePlaceholder")}
                    onChange={(event) => onChangeEircode(event.target.value)}
                />
                {errorMessage && (
                    <Typography color="error" variant="small">
                        {errorMessage}
                    </Typography>
                )}
            </Grid>
            <Grid alignItems="center" display="flex" item md={5} xs={12}>
                {!getEircodeAddressListState.isLoading && (
                    <LabeledButton
                        color="primary"
                        id={`${idPrefix}eircodeConfirmButton`}
                        labelText={`${t("common:confirm")} ${t("address:eircode")}`}
                        type="submit"
                        onClick={findAddress}
                    >
                        {t("common:confirm")} {t("address:eircode")}
                    </LabeledButton>
                )}
                {getEircodeAddressListState.isLoading && <Loader size={40} />}
            </Grid>
        </Grid>
    );
};
